// The message catalog. One flat, dotted-key `Messages` shape; the three locale
// catalogs are each typed `Messages`, so TypeScript fails the build if any
// locale is missing a key or has a stray one — locale parity is enforced at
// compile time, not left to a runtime check.
//
// Copy source of truth: bfc-command-center/docs/specs/bfc/website-content{,.es,.pt}.md
// (the approved trilingual marketing copy). Generic UI strings (form, nav,
// switcher) are translated in the same neutral, professional register.

export type Locale = 'en' | 'es' | 'pt-BR';

// Display order of the switcher.
export const LOCALES: readonly Locale[] = ['en', 'es', 'pt-BR'];

export interface Messages {
  // Home — hero
  'home.eyebrow': string;
  'home.lead': string;
  'home.sub': string;
  'home.cta': string;
  'home.cta2': string;
  // Home — highlight cards
  'home.card1.title': string;
  'home.card1.body': string;
  'home.card2.title': string;
  'home.card2.body': string;
  'home.card3.title': string;
  'home.card3.body': string;
  // Section — Who We Are
  'who.title': string;
  'who.p1': string;
  'who.p2': string;
  'who.p3': string;
  // Section — What We Do
  'what.title': string;
  'what.intro': string;
  'what.sourcing.title': string;
  'what.sourcing.body': string;
  'what.logistics.title': string;
  'what.logistics.body': string;
  'what.expertise.title': string;
  'what.expertise.body': string;
  // Section — Where We Operate
  'where.title': string;
  'where.intro': string;
  'where.us.name': string;
  'where.us.desc': string;
  'where.mx.name': string;
  'where.mx.desc': string;
  'where.sa.name': string;
  'where.sa.desc': string;
  'where.outro': string;
  // Section — What We Supply
  'supply.title': string;
  'supply.intro': string;
  'supply.core.title': string;
  'supply.core.btx.name': string;
  'supply.core.btx.desc': string;
  'supply.core.olefins.name': string;
  'supply.core.olefins.desc': string;
  'supply.core.solvents.name': string;
  'supply.core.solvents.desc': string;
  'supply.core.fuel.name': string;
  'supply.core.fuel.desc': string;
  'supply.refining.title': string;
  'supply.refining.catalysts.name': string;
  'supply.refining.catalysts.desc': string;
  'supply.refining.hydro.name': string;
  'supply.refining.hydro.desc': string;
  'supply.refining.process.name': string;
  'supply.refining.process.desc': string;
  'supply.specialty.title': string;
  'supply.specialty.additives.name': string;
  'supply.specialty.additives.desc': string;
  'supply.specialty.coatings.name': string;
  'supply.specialty.coatings.desc': string;
  'supply.specialty.custom.name': string;
  'supply.specialty.custom.desc': string;
  'supply.outro': string;
  // Section — Trusted Supply Relationships
  'partners.title': string;
  'partners.intro': string;
  'partners.basf': string;
  'partners.3m': string;
  'partners.honeywell': string;
  'partners.marcopolo': string;
  'partners.outro': string;
  // Section — Industries We Serve
  'industries.title': string;
  'industries.intro': string;
  'industries.oilgas': string;
  'industries.refiners': string;
  'industries.manufacturers': string;
  'industries.energy': string;
  'industries.government': string;
  'industries.outro': string;
  // Section — Logistics & Delivery
  'logistics.title': string;
  'logistics.intro': string;
  'logistics.formats.title': string;
  'logistics.formats.desc': string;
  'logistics.terminal.title': string;
  'logistics.terminal.desc': string;
  'logistics.compliance.title': string;
  'logistics.compliance.desc': string;
  'logistics.terms.title': string;
  'logistics.terms.desc': string;
  'logistics.rapid.title': string;
  'logistics.rapid.desc': string;
  'logistics.outro': string;
  'logistics.note': string;
  // Section — Why Bluefield Chemicals
  'why.title': string;
  'why.supply.title': string;
  'why.supply.body': string;
  'why.speed.title': string;
  'why.speed.body': string;
  'why.rules.title': string;
  'why.rules.body': string;
  'why.practical.title': string;
  'why.practical.body': string;
  'why.protect.title': string;
  'why.protect.body': string;
  // Section — For Buyers
  'buyers.title': string;
  'buyers.intro': string;
  'buyers.pain1': string;
  'buyers.pain2': string;
  'buyers.pain3': string;
  'buyers.pain4': string;
  'buyers.outro': string;
  'buyers.cta': string;
  // Section — Contact (inline)
  'info.title': string;
  'info.intro': string;
  'info.sales': string;
  'info.procurement': string;
  'info.trading': string;
  'info.phone': string;
  'info.offices': string;
  // Section — Closing
  'closing.statement': string;
  // Nav — section anchors
  'nav.about': string;
  'nav.supply': string;
  'nav.why': string;
  // Nav — existing
  'nav.home': string;
  'nav.contact': string;
  'nav.me': string;
  'nav.devices': string;
  'nav.passkeys': string;
  // Contact — intro
  'contact.title': string;
  'contact.sub': string;
  // Contact — form fields
  'contact.field.name': string;
  'contact.field.email': string;
  'contact.field.phone': string;
  'contact.field.subject': string;
  'contact.field.message': string;
  'contact.field.company': string;
  'contact.consent': string;
  'contact.submit': string;
  'contact.sending': string;
  // Contact — states
  'contact.validation': string;
  'contact.error.badRequest': string;
  'contact.error.rateLimited': string;
  'contact.error.server': string;
  'contact.error.network': string;
  'contact.success.title': string;
  'contact.success.lead': string;
  'contact.consentLink': string;
  // Footer
  'footer.privacy': string;
  // Privacy & cookies page (interim notice — issue #46 pulls the public half forward)
  'privacy.title': string;
  'privacy.updated': string;
  'privacy.interim': string;
  'privacy.intro': string;
  'privacy.who.title': string;
  'privacy.who.body': string;
  'privacy.collect.title': string;
  'privacy.collect.body': string;
  'privacy.use.title': string;
  'privacy.use.body': string;
  'privacy.store.title': string;
  'privacy.store.body': string;
  'privacy.retain.title': string;
  'privacy.retain.body': string;
  'privacy.rights.title': string;
  'privacy.rights.body': string;
  'privacy.cookies.title': string;
  'privacy.cookies.body': string;
  'privacy.requests.title': string;
  'privacy.requests.body': string;
  'privacy.requests.cta': string;
  // Locale switcher
  'locale.switcher.label': string;
  'locale.name.en': string;
  'locale.name.es': string;
  'locale.name.pt': string;
}

const en: Messages = {
  'home.eyebrow': 'Bluefield Chemicals',
  'home.lead': 'Securing supply. Moving markets. Delivering on every commitment.',
  'home.sub':
    'A petrochemical sourcing and trading partner for companies that cannot afford to run short.',
  'home.cta': 'Talk to our trade desk',
  'home.cta2': 'Request a quote',
  'home.card1.title': 'Supply secured',
  'home.card1.body':
    'Strong sourcing relationships and deep market knowledge let us find product even when supply is tight.',
  'home.card2.title': 'Speed when it counts',
  'home.card2.body':
    'In volatile markets, fast action protects your operation — and we are built to move quickly.',
  'home.card3.title': 'Trade handled',
  'home.card3.body':
    'Logistics, customs, and cross-border compliance, managed end to end so orders arrive complete and on time.',

  'who.title': 'Who We Are',
  'who.p1':
    'Bluefield Chemicals is a petrochemical distribution and trading company. In plain terms, we connect the manufacturers who produce essential industrial chemicals with the businesses that depend on them — and we manage everything in between: sourcing the product, negotiating the price, arranging transport, handling cross-border paperwork, and delivering on schedule.',
  'who.p2':
    'We operate across the United States, Mexico, and South America, with direct access to supply, storage, and cross-border transport. Our focus never changes: secure the product, control the logistics, and deliver on time — even when market conditions make that difficult.',
  'who.p3':
    'When supply tightens, when manufacturers ration how much each customer can buy, and when other suppliers fall short, our clients rely on Bluefield Chemicals to keep their operations moving.',

  'what.title': 'What We Do',
  'what.intro':
    'We make a complex, fast-moving market simple for our clients. Our work falls into three areas:',
  'what.sourcing.title': 'Sourcing & trading',
  'what.sourcing.body':
    'We find and secure the materials our clients need, including products that are hard to obtain or in short supply, and we structure each deal to make commercial sense.',
  'what.logistics.title': 'Logistics & delivery',
  'what.logistics.body':
    'We manage the movement of product from origin to destination — transport, storage, scheduling, and the documentation that crosses borders — so orders arrive complete and on time.',
  'what.expertise.title': 'Market & compliance expertise',
  'what.expertise.body':
    'We track pricing and availability, navigate the regulations that govern chemical trade, and give our clients clear guidance so they can plan with confidence.',

  'where.title': 'Where We Operate',
  'where.intro':
    'We work across three connected markets, with the relationships and infrastructure to source in one region and deliver in another:',
  'where.us.name': 'United States',
  'where.us.desc':
    'Strategic sourcing, export structuring, and close coordination with suppliers.',
  'where.mx.name': 'Mexico',
  'where.mx.desc':
    'Industrial distribution and supply to both private-sector and government buyers.',
  'where.sa.name': 'South America',
  'where.sa.desc': 'Market access, local partnerships, and reliable demand fulfillment.',
  'where.outro':
    'This cross-border reach lets us move quickly and find supply where others cannot.',

  'supply.title': 'What We Supply',
  'supply.intro':
    'We concentrate on the products that industry depends on most — high-demand materials that are critical to day-to-day operations.',
  'supply.core.title': 'Core Petrochemicals',
  'supply.core.btx.name': 'Aromatics (BTX)',
  'supply.core.btx.desc':
    'Benzene, toluene, and xylenes: foundational building blocks behind plastics, synthetic fibers, paints, adhesives, and packaging.',
  'supply.core.olefins.name': 'Olefins & derivatives',
  'supply.core.olefins.desc':
    'Ethylene, propylene, and related products: the raw materials for most plastics, films, and synthetic materials.',
  'supply.core.solvents.name': 'Industrial solvents',
  'supply.core.solvents.desc':
    'Liquids used to dissolve, clean, thin, or carry other substances in manufacturing, from coatings to cleaning processes.',
  'supply.core.fuel.name': 'Fuel components & additives',
  'supply.core.fuel.desc':
    'Blending materials and additives that improve the performance, stability, and quality of fuels.',
  'supply.refining.title': 'Refining & Process Materials',
  'supply.refining.catalysts.name': 'Catalysts & adsorbents',
  'supply.refining.catalysts.desc':
    'Catalysts speed up the chemical reactions at the heart of refining and manufacturing without being used up; adsorbents capture impurities to protect equipment and improve product quality.',
  'supply.refining.hydro.name': 'Hydroprocessing inputs',
  'supply.refining.hydro.desc':
    'Materials that help refiners remove sulfur and other impurities from fuels and feedstocks, meeting clean-fuel standards.',
  'supply.refining.process.name': 'Process optimization chemicals',
  'supply.refining.process.desc':
    'Specialty chemicals that keep industrial processes running efficiently, safely, and reliably.',
  'supply.specialty.title': 'Specialty & Performance Chemicals',
  'supply.specialty.additives.name': 'Industrial-grade additives',
  'supply.specialty.additives.desc':
    'Ingredients that enhance the performance of finished products.',
  'supply.specialty.coatings.name': 'Coatings & treatment solutions',
  'supply.specialty.coatings.desc':
    'Materials that protect, finish, or treat surfaces and equipment.',
  'supply.specialty.custom.name': 'Custom-sourced materials',
  'supply.specialty.custom.desc':
    'Hard-to-find or specification-specific products that we locate and secure on request.',
  'supply.outro':
    'If a material is hard to find, tightly regulated, or in short supply, that is exactly where we add the most value.',

  'partners.title': 'Trusted Supply Relationships',
  'partners.intro':
    'We work with globally recognized manufacturers and technology leaders, which gives our clients dependable access to quality product — even in constrained markets:',
  'partners.basf': 'BASF — One of the world\'s largest chemical producers.',
  'partners.3m': '3M — A global leader in materials science and industrial technology.',
  'partners.honeywell':
    'Honeywell UOP — A leading supplier of refining and petrochemical catalysts, adsorbents, and process technology.',
  'partners.marcopolo':
    'Marco Polo International — A global distributor of plastics and petrochemical products.',
  'partners.outro':
    'We use these relationships to secure supply when it is scarce and to deliver materials that are verified and specification-compliant — meaning every shipment meets the agreed standard.',

  'industries.title': 'Industries We Serve',
  'industries.intro':
    'We supply organizations that simply cannot afford a disruption to their supply:',
  'industries.oilgas': 'Oil & gas operators',
  'industries.refiners': 'Refiners and petrochemical plants',
  'industries.manufacturers': 'Industrial manufacturers',
  'industries.energy': 'Energy and infrastructure projects',
  'industries.government': 'Government and strategic procurement',
  'industries.outro':
    'For these clients, a missed delivery is not an inconvenience — it stops production. That is the standard we hold ourselves to.',

  'logistics.title': 'Logistics & Delivery',
  'logistics.intro':
    'For us, execution is everything. We manage the full chain so our clients don\'t have to:',
  'logistics.formats.title': 'Flexible delivery formats',
  'logistics.formats.desc': 'Bulk, ISO tank, and packaged shipments to match each order.',
  'logistics.terminal.title': 'Terminal and tank-farm coordination',
  'logistics.terminal.desc':
    'Reliable storage and handling at key points along the route.',
  'logistics.compliance.title': 'Cross-border compliance',
  'logistics.compliance.desc':
    'Customs, certifications, and documentation handled correctly the first time.',
  'logistics.terms.title': 'Clear trade terms',
  'logistics.terms.desc':
    'We work to standard international terms (FOB, CIF, and DDP) so every party knows exactly who is responsible for cost, transport, and risk at each stage.',
  'logistics.rapid.title': 'Rapid-response sourcing',
  'logistics.rapid.desc': 'Fast action when timelines are tight and supply is uncertain.',
  'logistics.outro':
    'When a problem comes up in transit, we solve it — and keep our clients informed every step of the way.',
  'logistics.note':
    'FOB (Free On Board) — the buyer takes over once goods are loaded at the origin port. CIF (Cost, Insurance & Freight) — we cover shipping and insurance to the destination port. DDP (Delivered Duty Paid) — we handle everything, including duties, right to the buyer\'s door.',

  'why.title': 'Why Bluefield Chemicals',
  'why.supply.title': 'We secure supply when others cannot.',
  'why.supply.body':
    'Strong sourcing relationships and market knowledge mean we find product in tight markets.',
  'why.speed.title': 'We move quickly.',
  'why.speed.body':
    'In volatile conditions, speed protects our clients — and we are built to act fast.',
  'why.rules.title': 'We understand the rules.',
  'why.rules.body':
    'Chemical trade is heavily regulated; we manage that complexity so our clients don\'t have to.',
  'why.practical.title': 'We deliver practical solutions.',
  'why.practical.body':
    'Our recommendations are commercially sound and ready to act on.',
  'why.protect.title': 'We protect your operations.',
  'why.protect.body':
    'Above all, we keep product flowing so our clients can keep running.',

  'buyers.title': 'For Buyers & Procurement Teams',
  'buyers.intro': 'If you are dealing with any of the following, we should talk:',
  'buyers.pain1': 'Limited allocations or capped order volumes',
  'buyers.pain2': 'Suppliers who don\'t deliver as promised',
  'buyers.pain3': 'Unpredictable pricing',
  'buyers.pain4': 'Urgent or hard-to-source requirements',
  'buyers.outro':
    'Bring us the challenge. We will tell you honestly what we can secure, at what price, and how quickly.',
  'buyers.cta': 'Contact our procurement team',

  'info.title': 'Contact',
  'info.intro': 'We\'d be glad to hear from you. Reach the right team directly:',
  'info.sales': 'Sales',
  'info.procurement': 'Procurement',
  'info.trading': 'Trade desk',
  'info.phone': 'Phone',
  'info.offices': 'Offices',

  'closing.statement':
    'Bluefield Chemicals works where supply is scarce and execution matters most. We don\'t compete on promises — we compete on delivery.',

  'nav.about': 'About',
  'nav.supply': 'Supply',
  'nav.why': 'Why BFC',
  'nav.home': 'Home',
  'nav.contact': 'Contact',
  'nav.me': 'Me',
  'nav.devices': 'Devices',
  'nav.passkeys': 'Passkeys',
  'contact.title': 'Talk to our trade desk',
  'contact.sub':
    'Bring us the challenge — what you need to source, and how fast. We will tell you honestly what we can secure, at what price, and how quickly.',
  'contact.field.name': 'Name',
  'contact.field.email': 'Email',
  'contact.field.phone': 'Phone (optional)',
  'contact.field.subject': 'Subject',
  'contact.field.message': 'Message',
  'contact.field.company': 'Company',
  'contact.consent': 'I consent to Bluefield Chemicals contacting me about this enquiry.',
  'contact.submit': 'Send message',
  'contact.sending': 'Sending…',
  'contact.validation': 'Please complete the required fields.',
  'contact.error.badRequest': 'Please check the form and try again.',
  'contact.error.rateLimited': 'Too many requests — give it a minute, then try again.',
  'contact.error.server': 'Something went wrong on our end. Please try again.',
  'contact.error.network': 'Could not reach the server. Check your connection.',
  'contact.success.title': 'Message sent',
  'contact.success.lead': 'Thanks for reaching out. A member of our team will be in touch shortly.',
  'contact.consentLink': 'Read our privacy notice',
  'footer.privacy': 'Privacy & cookies',
  'privacy.title': 'Privacy & cookies',
  'privacy.updated': 'Last updated: July 2, 2026',
  'privacy.interim':
    'This is an interim notice describing how we handle enquiry data today. A fuller policy will replace it as our lead-management tooling comes online.',
  'privacy.intro':
    'Bluefield Chemicals respects the people behind every enquiry. This page explains, in plain language, what personal data this website collects, why we collect it, and the choices you have.',
  'privacy.who.title': 'Who we are',
  'privacy.who.body':
    'Bluefield Chemicals is a petrochemical sourcing and trading company. For the personal data collected through this website, Bluefield Chemicals is the data controller.',
  'privacy.collect.title': 'What we collect',
  'privacy.collect.body':
    'When you contact our trade desk we ask for your name, work email, an optional phone number, a subject, and your message, together with a record of the consent you give. Our infrastructure also keeps standard security logs — such as the network address a request came from — to protect the service against abuse.',
  'privacy.use.title': 'How we use it',
  'privacy.use.body':
    'Contact details are used for one purpose: responding to your enquiry and following up on the business you asked about. Security logs are used only to detect and block abusive traffic. We do not sell personal data and we do not use it for advertising.',
  'privacy.store.title': 'Where it lives',
  'privacy.store.body':
    'Your data is stored in our database on Google Cloud (United States) and moves over encrypted connections end to end. Notification emails are delivered through our email provider. Access is restricted to the Bluefield Chemicals team.',
  'privacy.retain.title': 'How long we keep it',
  'privacy.retain.body':
    'We keep enquiry data while we work on your request and for as long as a business relationship may reasonably follow from it. Ask us to delete it and we will.',
  'privacy.rights.title': 'Your rights',
  'privacy.rights.body':
    'You may ask at any time to see, correct, or delete the personal data we hold about you. There is no form and no fee — tell us what you need and we will act on it.',
  'privacy.cookies.title': 'Cookies & local storage',
  'privacy.cookies.body':
    'The public site sets no advertising or analytics cookies and uses no third-party trackers. Your language choice is kept locally in your browser, and the site uses strictly necessary cookies for security — protecting forms against forgery and keeping staff sign-ins working. That is all.',
  'privacy.requests.title': 'Privacy requests',
  'privacy.requests.body':
    'To exercise any of these rights, reach us through the contact form and mention "privacy request" in the subject line — we will respond promptly.',
  'privacy.requests.cta': 'Go to the contact form',
  'locale.switcher.label': 'Language',
  'locale.name.en': 'English',
  'locale.name.es': 'Español',
  'locale.name.pt': 'Português',
};

export { en };

// ES and PT-BR catalogs live in messages-es.ts and messages-pt-BR.ts
// (lazy-loaded by locale.ts for code-splitting — keeps the main bundle under 30kB).

// CATALOGS is available for test-time key-parity checks only. It is NOT
// imported by production code (locale.ts uses dynamic import() instead).
const loadEs = (): Promise<Messages> => import('./messages-es').then((m) => m.default);
const loadPtBR = (): Promise<Messages> => import('./messages-pt-BR').then((m) => m.default);
export { loadEs as _testLoadEs, loadPtBR as _testLoadPtBR };

export type MsgKey = keyof Messages;
